import Conf from './Conf'
import Network from './Network'
import User from './User'
import Dot from './Dot'
import Log from './Log'
import PhpRegex from './PhpRegex'
import Survey from './Survey'
import mitt from 'mitt'
import Livechat from './Livechat'
import I18n from './I18n'
import DataLayer from '../chatbot/helpers/DataLayer'

let stateId = null
let stateData = {}
let stateSec = null
let emitter = null
let ttl = null
let dependencies = {}
let dependencyConf = {}
let questionsConf = {}
let chatbotEventBus = null
let saveTimeout = null

export default class State {
  static async init (eventBus) {
    chatbotEventBus = eventBus
    emitter = mitt()
    this.setupEvents()
    return await this.loadExistingState()
  }

  static async loadExistingState () {
    if (Conf.get().ignoreExistingState) {
      return false
    }

    const chatbotId = Conf.get().chatbotId
    const userId = User.id()
    const ret = await Network.getInstance().get(
      `${Conf.get().services.config}/chatbot/${chatbotId}/user/${userId}/state`
    )
    if (ret && ret.id !== undefined) {
      stateId = ret.id
      stateData = ret.data
      stateSec = ret.sec
      ttl = ret.ttl
      this.removeEditStepMessages()
      this.emitUpdate()
      return true
    }
    return false
  }

  static setupEvents () {
    /**
     * When refocusing the window, we must reload the state to see if we have new updates.
     */
    window.addEventListener(
      'focus',
      () => {
        this.loadExistingState()
      },
      false
    )
    document.addEventListener(
      'visibilitychange',
      () => {
        if (!document.hidden) {
          this.loadExistingState()
        }
      },
      false
    )
  }

  static id () {
    return stateId
  }

  static on (eventName, cb) {
    emitter.on(eventName, cb)

    switch (eventName) {
      case 'update':
        this.emitUpdate()
        break
    }
  }

  static off (eventName, cb) {
    emitter.off(eventName, cb)
  }

  static async createNewState (isReset = false) {
    const chatbotId = Conf.get().chatbotId
    const userId = User.id()
    const ret = await Network.getInstance().post(
      `${Conf.get().services.config}/chatbot/${chatbotId}/user/${userId}/state`
    )
    if (ret && ret.id !== undefined) {
      stateId = ret.id
      stateData = ret.data
      stateSec = ret.sec
      ttl = ret.ttl
      let presetState = {}
      if (Conf.get().visitorId !== null) {
        presetState.finalCustomerId = Conf.get().visitorId
      }
      presetState.mode = Conf.get().home.baseMode ?? 'index'
      presetState = {
        ...presetState,
        ...(Conf.get().presetState ?? {}),
        userContact: {
          firstname: Conf.get().userContactDetails.firstname ?? '',
          lastname: Conf.get().userContactDetails.lastname ?? '',
          email: Conf.get().userContactDetails.email ?? '',
          phone: Conf.get().userContactDetails.phone ?? ''
        }
      }
      if (Object.keys(presetState).length) {
        await this.setMany(presetState)
      } else {
        await this.emitUpdate()
      }
      if (isReset) {
        emitter.emit('reset', undefined)
      }
      return true
    }
    return false
  }

  static async set (key, value) {
    if (this.id() === null) {
      await this.createNewState()
    }
    stateData[key] = value
    await this.emitUpdate()
    this.save()
  }

  static async setMany (o) {
    if (this.id() === null) {
      await this.createNewState()
    }
    for (const key in o) {
      stateData[key] = o[key]
    }
    await this.emitUpdate()
    this.save()
  }

  static async append (key, value) {
    if (this.id() === null) {
      await this.createNewState()
    }
    if (stateData[key] === undefined) {
      stateData[key] = []
    }
    stateData[key].push(value)
    await this.emitUpdate()
    this.save()
  }

  static async appendMany (o) {
    if (this.id() === null) {
      await this.createNewState()
    }
    for (const key in o) {
      if (stateData[key] === undefined) {
        stateData[key] = []
      }
      stateData[key].push(o[key])
    }
    await this.emitUpdate()
    this.save()
  }

  static async setKey (o, key, value) {
    if (this.id() === null) {
      await this.createNewState()
    }
    if (stateData[o] === undefined) {
      stateData[o] = {}
    }
    stateData[o][key] = value
    await this.emitUpdate()
    this.save()
  }

  static async getKey (o, key, defaultValue = undefined) {
    if (this.id() === null) {
      await this.createNewState()
    }
    if (stateData[o] === undefined) {
      return defaultValue
    }
    return stateData[o][key] ?? defaultValue
  }

  static async get (key, defaultValue = undefined) {
    if (this.id() === null) {
      await this.createNewState()
    }
    return stateData[key] ?? defaultValue
  }

  static removeEditStepMessages () {
    Log.info('Remove edit-step messages')
    stateData.messages = stateData.messages.filter(
      m => m.type !== 'edit-step' && !m.temporary
    )
  }

  static persistTemporaryMessages () {
    if (stateData.messages) {
      Log.info('Persisting temporary messages')
      stateData.messages = stateData.messages.map(m => {
        m.temporary = false
        return m
      })
    }
  }

  static ensureRepliesAreObject () {
    if (stateData.replies instanceof Array) {
      let o = {}
      for (const key in stateData.replies) {
        if (/^[0-9]+$/.test(key + '') === false) o[key] = stateData.replies[key]
      }
      stateData.replies = o
    }
  }

  static getReplies () {
    return stateData.replies ?? {}
  }

  static getParsedReplies (filter) {
    let ret = {}
    let data = stateData.replies ?? {}

    for (const key in data) {
      let questionRefersTo = null
      let val = null
      if (data[key] instanceof Array) {
        val = []
        for (const el of data[key]) {
          questionRefersTo = el.questionRefersTo ?? null
          let o = {
            name: el.name,
            slug: el.slug,
            date: el.date
          }
          if (el.ref !== null) {
            o.ref = el.ref
          }
          val.push(o)
        }
      } else {
        questionRefersTo = data[key].questionRefersTo ?? null
        let o = {
          name: data[key].name,
          slug: data[key].slug,
          date: data[key].date
        }
        if (data[key].ref !== null) {
          o.ref = data[key].ref
        }
        val = o
      }
      if (val !== null) {
        ret[key] = {
          ref: questionRefersTo,
          value: val
        }
      }
    }

    if (filter !== undefined && filter.all === false) {
      ret = Object.fromEntries(
        Object.entries(ret).filter(item => {
          return filter.list.includes(item[0])
        })
      )
    }

    return ret
  }

  static getApiCalls () {
    return stateData.apicalls ?? {}
  }

  static async saveApiCall (questionSlug, result, duration) {
    // Ensure replies are an object
    this.ensureRepliesAreObject()

    if (stateData.apicalls instanceof Array) {
      let o = {}
      for (const key in stateData.apicalls) {
        if (/^[0-9]+$/.test(key + '') === false) o[key] = stateData.replies[key]
      }
      stateData.apicalls = o
    }
    stateData.apicalls[questionSlug] = {
      date: +new Date(),
      duration,
      result
    }
    stateData.started = true
    await this.refreshDependencies()
    this.persistTemporaryMessages()
    this.removeEditStepMessages()
    this.save()
  }

  static async saveReply (questionSlug, answer) {
    // Ensure replies are an object
    this.ensureRepliesAreObject()

    const questionConf = Survey.getCurrentQuestion()
    const stepName = Survey.getCurrentQuestionName()
    stateData.replies[questionSlug] = {
      name:
        typeof answer === 'object' && answer.name !== undefined
          ? answer.name
          : answer,
      slug:
        typeof answer === 'object' && answer.slug !== undefined
          ? answer.slug
          : answer,
      ref: typeof answer === 'object' ? answer.refersTo ?? null : null,
      date: +new Date(),
      survey: questionConf.$survey ?? {},
      stepName,
      questionRefersTo: questionConf.refersTo ?? null
    }
    DataLayer.push({
      event: `noci:reply:${questionSlug}`,
      [`noci:reply:${questionSlug}`]: stateData.replies[questionSlug].slug
    })
    stateData.started = true
    await this.refreshDependencies()
    this.persistTemporaryMessages()
    this.removeEditStepMessages()
    this.save()
    Conf.get().onReply({
      replies: stateData.replies,
      reply: stateData.replies[questionSlug],
      question: questionSlug
    })
  }

  static async saveReplies (questionSlug, answers) {
    // Ensure replies are an object
    this.ensureRepliesAreObject()

    const questionConf = Survey.getCurrentQuestion()
    const stepName = Survey.getCurrentQuestionName()
    stateData.replies[questionSlug] = answers.map(answer => ({
      name:
        typeof answer === 'object' && answer.name !== undefined
          ? answer.name
          : answer,
      slug:
        typeof answer === 'object' && answer.slug !== undefined
          ? answer.slug
          : answer,
      ref: typeof answer === 'object' ? answer.refersTo ?? null : null,
      date: answer.date ?? +new Date(),
      survey: questionConf.$survey ?? {},
      stepName,
      questionRefersTo: questionConf.refersTo ?? null
    }))
    DataLayer.push({
      event: `noci:reply:${questionSlug}`,
      [`noci:reply:${questionSlug}`]: stateData.replies[questionSlug]
        .map(r => r.slug ?? '')
        .filter(r => r.length)
        .join(',')
    })
    stateData.started = true
    await this.refreshDependencies()
    this.persistTemporaryMessages()
    this.removeEditStepMessages()
    this.save()
    Conf.get().onReply({
      replies: stateData.replies,
      reply: stateData.replies[questionSlug],
      question: questionSlug
    })
  }

  static save () {
    if (saveTimeout !== null) {
      clearTimeout(saveTimeout)
    }
    saveTimeout = setTimeout(async _ => {
      const res = await Network.getInstance().put(
        `${Conf.get().services.config}/state/${stateId}/${stateSec}`,
        stateData
      )
      ttl = res.ttl
    }, 500)
  }

  static async addMessage (params) {
    const data = {
      type: 'message',
      from: 'chatbot',
      step: null,
      fromId: null,
      agent: null,
      data: {},
      editable: false,
      editData: null,
      temporary: false,
      avoidDoubleMessage: false,
      seen: {
        status: true,
        date: +new Date()
      },
      date: +new Date(),
      ...params
    }

    if (data.step === null) {
      Log.error('Cannot add message, step is null', data)
      return false
    }

    if (data.avoidDoubleMessage) {
      const messages = await State.get(Survey.getStateKey(), [])
      if (messages.length && messages[messages.length - 1].step === data.step) {
        Log.info(
          `Cannot add message, because it was already displayed from step ${data.step}.`
        )
        return false
      }
    }

    if (data.delay !== undefined) {
      chatbotEventBus.emit('loading', true)
      await new Promise((resolve, reject) => {
        setTimeout(_ => {
          resolve()
        }, data.delay * Conf.get().autoDelayRatio)
      })
      chatbotEventBus.emit('loading', false)
    }

    Log.info('Message added', data)

    await State.append(Survey.getStateKey(), data)
    emitter.emit('new_message', data)
    this.save()
  }

  static async setDependencyValue (key, value) {
    dependencies[key] = value

    /*
     * Handle saved dependencies
     * We save some dependencies in the state as they are session related and must be handled on chatbot refresh
     */
    switch (key) {
      case 'agent_timeout':
      case 'agent_handled':
      case 'satisfied':
      case 'not_satisfied':
        await this.setKey('savedDependencies', key, value)
        break
    }
  }

  static async refreshDependencies () {
    let newDependencies = {
      ...(Conf.get().dependencies ?? {}),
      user_connected: Conf.get().visitorId !== null,
      user_not_connected: Conf.get().visitorId === null,
      agent_timeout:
        stateData?.savedDependencies?.agent_timeout === undefined
          ? null
          : stateData.savedDependencies.agent_timeout,
      agent_handled:
        stateData?.savedDependencies?.agent_handled === undefined
          ? null
          : stateData.savedDependencies.agent_handled,
      satisfied:
        stateData?.savedDependencies?.satisfied === undefined
          ? null
          : stateData.savedDependencies.satisfied,
      not_satisfied:
        stateData?.savedDependencies?.not_satisfied === undefined
          ? null
          : stateData.savedDependencies.not_satisfied
    }

    newDependencies.agent_available = await Livechat.agentsAvailable()
    newDependencies.no_agent_available = !newDependencies.agent_available

    for (const dependencyKey in dependencyConf) {
      let dependency = dependencyConf[dependencyKey]
      if (Array.isArray(dependency)) {
        dependency = {
          operator: 'AND',
          children: dependency
        }
      }

      newDependencies[dependencyKey] = this.testRootDependency(dependency)
    }

    dependencies = newDependencies
  }

  static testRootDependency (rootDependency) {
    const list = rootDependency.children

    if (list.length === 0) return true

    let results = {
      ok: 0,
      nok: 0
    }

    for (const dep of list) {
      if (dep.question !== undefined) {
        const ret = this.testDependency(dep)

        if (ret === null) {
          continue
        }

        ++results[ret ? 'ok' : 'nok']
      } else if (dep.children !== undefined) {
        ++results[this.testRootDependency(dep) ? 'ok' : 'nok']
      }
    }

    return (
      results.ok > 0 && (rootDependency.operator === 'OR' || results.nok === 0)
    )
  }

  static testDependency (dependency) {
    let question = questionsConf[dependency.question]

    if (['$event', '$lang'].includes(dependency.question)) {
      question = {
        type: dependency.question
      }
    }

    if (question === undefined) {
      return null
    }

    /**
     * If the question is not replied yet, returns null
     */
    if (
      stateData?.replies[dependency.question] === undefined &&
      question.type !== 'results' &&
      question.type !== 'apicall' &&
      dependency.question !== '$event' &&
      dependency.question !== '$lang'
    ) {
      return null
    }

    /**
     * If the condition is not defined, set it to "eq"
     */
    if (dependency.condition === undefined) {
      dependency.condition = 'eq'
    }

    /**
     * Gets the value. To easier management, we get the value as an array
     * to be able to test only with Array.findIndex
     */
    let value = null
    if (question.type === 'results') {
      value = { slug: stateData.results.length }
    } else if (question.type === 'apicall') {
      if (question.receiveFormat === 'text')
        value = { slug: stateData.apicalls[dependency.question]?.result }
      else
        value = {
          slug: Dot.get(
            stateData.apicalls[dependency.question]?.result,
            dependency.path
          )
        }
    } else if (question.type === '$event') {
      value = stateData.events.map(e => {
        return {
          slug: e.type
        }
      })
    } else if (question.type === '$lang') {
      value = [{ slug: I18n.locale() }]
    } else {
      value = stateData.replies[dependency.question]
    }
    if (value instanceof Array === false) value = [value]

    Log.debug('Testing condition : ', dependency, value)

    /**
     * Test all conditions
     */
    switch (dependency.condition) {
      case 'eq':
        return value.findIndex(answer => dependency.answer === answer.slug) > -1
        break
      case 'ne':
        return value.findIndex(answer => dependency.answer !== answer.slug) > -1
        break
      case 'lt':
        return value.findIndex(answer => dependency.answer > answer.slug) > -1
        break
      case 'lte':
        return value.findIndex(answer => dependency.answer >= answer.slug) > -1
        break
      case 'gt':
        return value.findIndex(answer => dependency.answer < answer.slug) > -1
        break
      case 'gte':
        return value.findIndex(answer => dependency.answer <= answer.slug) > -1
        break
      case 'regex':
        return (
          value.findIndex(answer =>
            PhpRegex.parse(dependency.answer).test(answer.slug + '')
          ) > -1
        )
        break
    }
    return null
  }

  static async resolveDependency (dependsOn) {
    if (typeof dependsOn === 'string') {
      dependsOn = [dependsOn]
    }

    let rootDependency = dependsOn
    if (Array.isArray(rootDependency)) {
      rootDependency = {
        operator: 'AND',
        children: dependsOn
      }
    }

    return this.isRootDependencyResolved(rootDependency)
  }

  static isRootDependencyResolved (rootDependency) {
    const resolvedDependencies = rootDependency.children.filter(dep => {
      if (typeof dep === 'string') {
        return dependencies[dep]
      }

      return this.isRootDependencyResolved(dep)
    })

    if (rootDependency.operator === 'OR') {
      return resolvedDependencies.length > 0
    }

    return resolvedDependencies.length === rootDependency.children.length
  }

  static setDependencies (conf) {
    dependencyConf = conf
  }

  static setQuestions (conf) {
    questionsConf = conf
  }

  static async emitUpdate () {
    await this.refreshDependencies()
    emitter.emit('update', stateData)
  }
}
